import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactNode } from "react";
import { useForm, type UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { type capSchema, MAX_TOKENS } from "./tokensSchema";

import { Button } from "@/components/ui/v2/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import { cn } from "@/lib/utils/cn";

type TokensSchema = ReturnType<typeof capSchema>;
export type TokensInput = { tokens: number | null };
export type TokensSetError = UseFormSetError<TokensInput>;

type InlineTokensEditorProps = {
  schema: TokensSchema;
  label: string;
  defaultValue: number | null;
  help: (tokens: number) => ReactNode;
  isPending: boolean;
  onSave: (tokens: number, setError: TokensSetError) => void;
  onCancel: () => void;
  prefix?: string | false;
};

export function InlineTokensEditor({
  schema,
  label,
  defaultValue,
  help,
  isPending,
  onSave,
  onCancel,
  prefix = false,
}: InlineTokensEditorProps) {
  const { t } = useTranslation();
  const form = useForm<TokensInput, unknown, z.output<TokensSchema>>({
    resolver: zodResolver(schema),
    defaultValues: { tokens: defaultValue },
    mode: "onChange",
  });
  const tokens = form.watch("tokens") ?? 0;
  const submit = form.handleSubmit(({ tokens: value }) => onSave(value, form.setError));

  const error = form.formState.errors.tokens?.message;

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !isPending) {
            e.preventDefault();
            onCancel();
          }
        }}
        noValidate
        className="flex flex-col gap-2"
      >
        <FormField
          control={form.control}
          name="tokens"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <FormattedCurrencyInput
                  aria-label={label}
                  value={field.value ?? undefined}
                  onChange={(value) => field.onChange(value ?? null)}
                  onBlur={field.onBlur}
                  prefix={prefix}
                  suffix={t("captainBilling.editors.unit")}
                  maxValue={MAX_TOKENS}
                  inputMode="numeric"
                  placeholder="0"
                  disabled={isPending}
                  autoFocus
                  className="h-11 text-2xl font-semibold tracking-tight tabular-nums"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex items-center justify-between gap-3">
          <p
            role={error ? "alert" : undefined}
            className={cn("min-w-0 text-sm tabular-nums", error ? "text-destructive" : "text-muted-foreground")}
          >
            {error ?? help(Math.max(0, tokens))}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" size="sm" loading={isPending} disabled={!form.formState.isValid}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
